import React from "react";
import { IMedia, MediaAccessType } from "@/store/models/video.models";
import editIcon from "@/assets/icons/edit-light.svg";
import trashIcon from "@/assets/icons/trash.svg";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import minusIcon from "@/assets/icons/cross.svg";
// import VideoPlayer from "@/components/VideoPlayer/VideoPlayer";
import plusIcon from "@/assets/icons/plus.svg";
import styles from "./VideoCard.module.scss";
import { FormControlLabel, Skeleton, Switch } from "@mui/material";
import {
  useRegenerateThumbnailMutation,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";
import { Button, notification } from "antd";

type Props = {
  video: IMedia;
  handleToggleEditModal?: (video: IMedia) => void;
  handleToggleDeleteModal?: (video: IMedia) => void;
  handleTogglePublishModal?: (video: IMedia) => void;
  handleToggleDeleteVideoModal?: (video: IMedia) => void;
  isEdit: boolean;
  isFeatured?: boolean;
  isLoading?: boolean;
};

export const VideoCard = ({
  video,
  handleToggleEditModal,
  handleToggleDeleteModal,
  handleTogglePublishModal,
  handleToggleDeleteVideoModal,
  isEdit,
  isFeatured,
  isLoading,
}: Props) => {
  const [updateVideo] = useUpdateMediaMutation();
  const [updateMediaVideo] = useUpdateMediaMutation();
  const [regenerateThumbnail, { isLoading: isLoadingRegenerate }] =
    useRegenerateThumbnailMutation();

  const handleUpdateIsRecommended = async () => {
    try {
      const updatedSections = video.sections.includes("Recommendation")
        ? video.sections.filter((section) => section !== "Recommendation")
        : [...video.sections, "Recommendation"];
      await updateVideo({
        uid: video.uid,
        sections: updatedSections,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update the video:", error);
    }
  };

  const handleChangePremium = async () => {
    try {
      const newAccessType =
        video.accessType === MediaAccessType.BASE
          ? MediaAccessType.STORYTELLER_BASIC
          : MediaAccessType.BASE;

      await updateMediaVideo({
        uid: video.uid,
        accessType: newAccessType,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update the video:", error);
    }
  };

  const handleRegenerateThumbnail = async () => {
    try {
      await regenerateThumbnail({ mediaUid: video.uid }).unwrap();
    } catch (error: any) {
      console.error("Failed to regenerate thumbnail:", error);
      notification.error({
        message: "Failed to regenerate thumbnail",
        description: error,
      });
    }
  };

  return (
    <div className={styles.item} key={video.uid}>
      <div className={styles.itemHeader}>
        <h5 className={styles.itemTitle}>{video.title}</h5>
        <div className={styles.itemHeaderBtns}>
          {isLoading ? (
            <>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </>
          ) : (
            <>
              <div className={styles.itemHeaderLikes}>
                <FavoriteBorderOutlinedIcon />
                <span>{video.likes}</span>
              </div>
              <div className={styles.itemHeaderLikes}>
                <VisibilityOutlinedIcon />
                <span>{video.viewsCount}</span>
              </div>
              {isEdit && handleToggleEditModal && (
                <button
                  className={styles.itemHeaderEditBtn}
                  onClick={() => handleToggleEditModal(video)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={editIcon}
                    alt="edit"
                  />
                </button>
              )}
              {handleToggleDeleteVideoModal && (
                <button
                  className={styles.itemHeaderAddBtn}
                  onClick={() => handleToggleDeleteVideoModal(video)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={
                      video.sections.includes("Recommendation")
                        ? minusIcon
                        : plusIcon
                    }
                    alt="add item"
                  />
                </button>
              )}
              {isFeatured && (
                <button
                  className={styles.itemHeaderAddBtn}
                  onClick={handleUpdateIsRecommended}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={
                      video.sections.includes("Recommendation")
                        ? minusIcon
                        : plusIcon
                    }
                    alt={`${video.sections.includes("Recommendation") ? "remove item" : "add item"}`}
                  />
                </button>
              )}
              {handleToggleDeleteModal && (
                <button
                  className={styles.itemHeaderDeleteBtn}
                  onClick={() => handleToggleDeleteModal(video)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={trashIcon}
                    alt="delete"
                  />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!isLoading && (
        <div className={styles.premium}>
          <FormControlLabel
            control={
              <Switch
                checked={video.accessType !== MediaAccessType.BASE}
                onChange={handleChangePremium}
                sx={{
                  "& .css-byenzh-MuiButtonBase-root-MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track":
                    {
                      backgroundColor:
                        video.accessType !== MediaAccessType.BASE
                          ? "#008000"
                          : "#3f51b5",
                    },
                  "& .MuiSwitch-thumb": {
                    backgroundColor:
                      video.accessType !== MediaAccessType.BASE
                        ? "#008000"
                        : "#3f51b5",
                    boxShadow: "#FF000066",
                  },
                }}
              />
            }
            label="Premium"
          />

          {handleTogglePublishModal && (
            <FormControlLabel
              control={
                <Switch
                  checked={video.isPublished}
                  onChange={() => handleTogglePublishModal(video)}
                  sx={{
                    "& .css-byenzh-MuiButtonBase-root-MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track":
                      {
                        backgroundColor: "#3f51b5",
                      },
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#3f51b5",
                      boxShadow: "#FF000066",
                    },
                  }}
                />
              }
              label="Published"
            />
          )}

          <Button
            className={styles.regenerate}
            onClick={handleRegenerateThumbnail}
            loading={isLoadingRegenerate}
          >
            Regenerate thumbnail
          </Button>
        </div>
      )}
      <div className={styles.itemDescr}>{video.description}</div>

      {/* <VideoPlayer
        src={video.url as string}
        posterSrc={video.videoImgUrl as string}
      /> */}
      {!isLoading ? (
        <img
          src={video.thumbnailUrl}
          alt={video.thumbnailUrl}
          className={styles.itemImg}
        />
      ) : (
        <div className={styles.loaderContainer}>
          <Skeleton variant="rounded" width="100%" height="300px" />
        </div>
      )}
    </div>
  );
};
